import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import BackLink from "@/components/BackLink.jsx";
import { getFileUrl, getProduct, getProductReviews } from "@/api/public.api.js";
import { PRODUCT_PAGE_TITLE_SUFFIX, PRODUCT_ROUTE, STORE_NAME } from "@/utils/constants.js";
import { formatRatingOutOfFive } from "@/utils/format.js";
import "./product.css";
import "./product-reviews.css";

const SORT_OPTIONS = [
    { value: "GRADE_DESC", label: "Meilleures notes" },
    { value: "GRADE_ASC", label: "Notes les plus basses" },
    { value: "DATE_DESC", label: "Plus récents" },
    { value: "DATE_ASC", label: "Plus anciens" }
];

function formatReviewDate(value) {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function StarRating({ grade }) {
    const value = Math.max(0, Math.min(5, Number(grade) || 0));
    return (
        <span className="review-stars" aria-label={`${value} sur 5`}>
            {"★".repeat(Math.round(value))}
            <span className="review-stars-empty">{"★".repeat(5 - Math.round(value))}</span>
        </span>
    );
}

export default function ProductReviewsPage() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const sort = searchParams.get("sort") || "GRADE_DESC";
    const [product, setProduct] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [averageGrade, setAverageGrade] = useState(0);
    const [reviewCount, setReviewCount] = useState(0);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getProduct(id)
            .then((data) => setProduct(data.data))
            .catch((err) => setError(err.message));
    }, [id]);

    useEffect(() => {
        if (!product?.title) {
            return undefined;
        }
        document.title = `${product.title} — ${PRODUCT_PAGE_TITLE_SUFFIX}`;
        return () => {
            document.title = STORE_NAME;
        };
    }, [product?.title]);

    useEffect(() => {
        setLoading(true);
        getProductReviews(id, { sort, page: 0, size: 50 })
            .then((data) => {
                setReviews(data.data || []);
                setAverageGrade(data.averageGrade ?? 0);
                setReviewCount(data.reviewCount ?? 0);
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [id, sort]);

    const handleSortChange = (event) => {
        const next = event.target.value;
        setSearchParams(next === "GRADE_DESC" ? {} : { sort: next });
    };

    return (
        <main className="container product-reviews-page">
            <BackLink to={`${PRODUCT_ROUTE}/${id}`} className="product-page-back">
                Retour au produit
            </BackLink>
            {error && <p className="error">{error}</p>}
            <header className="product-reviews-header card">
                <div>
                    <h1>Avis{product ? ` — ${product.title}` : ""}</h1>
                    <p className="muted">
                        {reviewCount > 0
                            ? `${formatRatingOutOfFive(averageGrade)} · ${reviewCount} avis`
                            : "Aucun avis pour le moment"}
                    </p>
                    {reviewCount > 0 && <StarRating grade={averageGrade} />}
                </div>
                <label className="field product-reviews-sort">
                    Trier par
                    <select value={sort} onChange={handleSortChange}>
                        {SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
            </header>
            {loading && <p className="muted">Chargement des avis...</p>}
            {!loading && reviews.length === 0 && (
                <p className="muted">Aucun avis publié pour ce produit.</p>
            )}
            <div className="product-reviews-list">
                {reviews.map((review) => (
                    <article key={review.id} className="card product-review-card">
                        <div className="product-review-card-top">
                            <div className="product-review-author">
                                {review.authorPhotoId ? (
                                    <img
                                        src={getFileUrl(review.authorPhotoId)}
                                        alt=""
                                        className="product-review-avatar"
                                    />
                                ) : (
                                    <span className="product-review-avatar product-review-avatar--empty" aria-hidden="true">
                                        {(review.authorName || "A")[0]?.toUpperCase()}
                                    </span>
                                )}
                                <div>
                                    <strong>{review.anonymous || !review.authorName ? "Client anonyme" : review.authorName}</strong>
                                    <p className="muted">{formatReviewDate(review.createdAt)}</p>
                                </div>
                            </div>
                            <StarRating grade={review.grade} />
                        </div>
                        {review.comment && <p className="product-review-comment">{review.comment}</p>}
                    </article>
                ))}
            </div>
        </main>
    );
}
